import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import {
  CARD_PREVIEW_MESSAGE_TYPE,
  CARD_PREVIEW_READY_MESSAGE_TYPE,
  type CardPreviewSnapshot,
  type DigitalCardSettingsData,
  type PreviewEmployeePayload,
  type WalletSettingsData,
} from "../card-digital.interface";
import { generateEmployeeHash } from "../digital-card-link.service";

@Component({
  selector: "app-card-digital-preview",
  templateUrl: "./card-preview.component.html",
  styleUrls: ["./card-preview.component.scss"],
  standalone: false,
})
export class CardDigitalPreviewComponent implements OnChanges, OnDestroy {
  @Input() previewUrl = "";
  @Input() snapshot: CardPreviewSnapshot | null = null;
  @Input() bannerImageUrl: string | null = null;
  @Input() omitEmployeeFromPreview = false;
  @Input() placeholderText = "";
  @Input() previewSurface: "card" | "wallet" = "card";
  @Input() loading = false;
  @Input() showAppleWallet = true;
  @Input() showGoogleWallet = true;

  @ViewChild("previewFrame", { static: false })
  previewFrame?: ElementRef<HTMLIFrameElement>;

  sanitizedUrl: SafeResourceUrl | null = null;
  busy = false;

  private iframeReady = false;
  private lastSentKey = "";
  private destroyed = false;
  private sendGeneration = 0;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  get showOverlay(): boolean {
    return (
      this.loading || this.busy || (!!this.sanitizedUrl && !this.iframeReady)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["previewUrl"]) {
      const url = this.previewUrl.trim();
      this.sanitizedUrl = url
        ? this.sanitizer.bypassSecurityTrustResourceUrl(url)
        : null;
      this.iframeReady = false;
      this.lastSentKey = "";
    }
    void this.sendPreview();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  @HostListener("window:message", ["$event"])
  onWindowMessage(event: MessageEvent): void {
    const frame = this.previewFrame?.nativeElement;
    if (!frame?.contentWindow || event.source !== frame.contentWindow) {
      return;
    }
    if (
      typeof event.data !== "object" ||
      event.data === null ||
      (event.data as { type?: string }).type !== CARD_PREVIEW_READY_MESSAGE_TYPE
    ) {
      return;
    }
    this.iframeReady = true;
    void this.sendPreview();
  }

  private async sendPreview(): Promise<void> {
    if (this.destroyed || !this.iframeReady) {
      return;
    }
    const frame = this.previewFrame?.nativeElement;
    if (!frame?.contentWindow || !this.previewUrl.trim()) {
      return;
    }

    const settings = this.snapshot?.settings ?? {};
    const walletSettings = this.snapshot?.walletSettings ?? null;
    let employee: PreviewEmployeePayload | undefined;
    if (!this.omitEmployeeFromPreview && this.snapshot?.employee != null) {
      const source = this.snapshot.employee;
      employee = {
        fullName: source.fullName,
        jobTitle: source.jobTitle,
        userAbout: source.userAbout,
        email: source.email,
        gender: source.gender,
        countryOfOrigin: source.countryOfOrigin,
        phoneNumber: source.phoneNumber,
        mobileNumber: source.mobileNumber,
        calendarLink: source.calendarLink,
        picture: source.picture,
        thumbnailPhotoUrl: source.thumbnailPhotoUrl,
        customLinks: source.customLinks,
        dynamicVariables: source.dynamicVariables,
        variableLabels: source.variableLabels,
        variableIcons: source.variableIcons,
        companyData: source.companyData,
        brandData: source.brandData,
      };
    }

    const key = JSON.stringify({
      settings,
      walletSettings,
      bannerImageUrl: this.bannerImageUrl ?? null,
      previewSurface: this.previewSurface,
      showAppleWallet: this.showAppleWallet,
      showGoogleWallet: this.showGoogleWallet,
      employee,
    });
    if (key === this.lastSentKey) {
      return;
    }

    const generation = ++this.sendGeneration;
    this.busy = true;
    this.cdr.markForCheck();
    try {
      const email = employee?.email?.trim().toLowerCase() ?? "";
      const payload: {
        type: typeof CARD_PREVIEW_MESSAGE_TYPE;
        settings: DigitalCardSettingsData | Record<string, unknown>;
        bannerImageUrl: string | null;
        previewSurface: "card" | "wallet";
        walletSettings: WalletSettingsData | null;
        showAppleWallet: boolean;
        showGoogleWallet: boolean;
        employee?: PreviewEmployeePayload;
        hash?: string;
      } = {
        type: CARD_PREVIEW_MESSAGE_TYPE,
        settings,
        bannerImageUrl: this.bannerImageUrl ?? null,
        previewSurface: this.previewSurface,
        walletSettings,
        showAppleWallet: this.showAppleWallet,
        showGoogleWallet: this.showGoogleWallet,
      };
      if (employee) {
        payload.employee = employee;
      }
      if (email) {
        payload.hash = await generateEmployeeHash(email);
      }
      if (
        this.destroyed ||
        !this.iframeReady ||
        generation !== this.sendGeneration
      ) {
        return;
      }

      frame.contentWindow.postMessage(payload, "*");
      this.lastSentKey = key;
    } finally {
      if (generation === this.sendGeneration) {
        this.busy = false;
        this.cdr.markForCheck();
      }
    }
  }
}
